import React, { lazy, Suspense } from "react";
import { Route, Routes, Navigate, useLocation } from "react-router";
import LoggedInRoute from "./routing/LoggedInRoute";
import "react-big-calendar/lib/css/react-big-calendar.css";
import PageLoader from "./Components/PageLoader/PageLoader";
import SeoRouteManager from "./Components/SEO/SeoRouteManager";
import RouteForAdmin from "./routing/AdminRoutes";
import RouteForL1 from "./routing/L1Routes";
import RouteForL2 from "./routing/L2Routes";
import ConsultationList from "./Pages/Dashboard/admin/ConsultationList/ConsultationList";
import ContactList from "./Pages/Dashboard/admin/ConsultationList/ContactList";
import EarlyAccessList from "./Pages/Dashboard/admin/ConsultationList/EarlyAccessList";
import EarlyAccess from "./Pages/OtherPages/EarlyAccessPage";
import EvaluationForm from "./Pages/EvaluationForm/EvaluationForm";
import { GoogleOAuthProvider } from "@react-oauth/google";
import FeatureRequestPage from "./Components/FeatureIdeaPage/FeatureRequestPage";
import RedirectForm from "./Pages/OtherPages/RedirectForm";
import PasswordResetForm from "./Pages/OtherPages/PasswordResetForm";
import RouteForSuperAdmin from "./routing/SuperAdminRoutes";
import OrganisationManagement from "./Pages/Dashboard/admin/OrganisationManagement/OrganisationManagement";
import Pricing from "./Pages/pricing/Pricing";
import { seoPageRoutes } from "./Pages/SeoPages/seoPageData";
import { pSeoPageRoutes } from "./Pages/SeoPages/pSeoPageLoader";
// const EarlyAccessPage = lazy(() => import('./Pages/OtherPages/EarlyAccessPage'));
const Consultation = lazy(() => import("./Pages/OtherPages/Consultation"));
const TermsCondition = lazy(() => import("./Pages/OtherPages/TermsCondition"));
const PrivacyPolicy = lazy(() => import("./Pages/OtherPages/PrivacyPolicy"));
const AboutUs = lazy(() => import("./Pages/OtherPages/AboutUs"));
const ContactUs = lazy(() => import("./Pages/OtherPages/ContactUs"));
const DivisionsManagement = lazy(
  () =>
    import("./Pages/Dashboard/admin/DivisionDepartmentManagement/DivisionManagement"),
);
const DepartmentsManagement = lazy(
  () =>
    import("./Pages/Dashboard/admin/DivisionDepartmentManagement/DepartmentManagement"),
);
const CostBenefitManagement = lazy(
  () => import("./Pages/Dashboard/admin/ThemeManagement/CostBenefitCategories"),
);
const ThemeManagement = lazy(
  () => import("./Pages/Dashboard/admin/ThemeManagement/ThemeManagement"),
);
const MetricManagement = lazy(
  () => import("./Pages/Dashboard/admin/ThemeManagement/MetricManagement"),
);
const MyReports = lazy(() => import("./Pages/MyReports/MyReports"));
const Home = lazy(() => import("./Pages/Home/Home"));
const Login = lazy(() => import("./Pages/Auth/Login"));
const EmployeeDashboard = lazy(
  () => import("./Pages/Dashboard/Employee/EmployeeDashboard"),
);
const EmployeeDashboardHome = lazy(
  () =>
    import("./Pages/Dashboard/Employee/EmployeeDashboardHome/EmployeeDashboardHome"),
);
const EmployeeTasks = lazy(
  () => import("./Pages/Dashboard/Employee/EmployeeTasks/EmployeeTasks"),
);
const EmployeeProjects = lazy(
  () => import("./Pages/Dashboard/Employee/EmployeeProjects/EmployeeProjects"),
);
const EmployeeProjectDetails = lazy(
  () =>
    import("./Pages/Dashboard/Employee/EmployeeProjectsDetails/EmployeeProjectDetails"),
);
const FreelancerMessenger = lazy(
  () => import("./Pages/Dashboard/Employee/Messenger/EmployeeMessenger"),
);
const DrawingBoard = lazy(
  () => import("./Pages/Draw/DrawingBoard/DrawingBoard"),
);
const GenerateIdea = lazy(
  () => import("./Pages/Dashboard/GenerateIdea/GenerateIdea"),
);
const IdeaReport = lazy(
  () => import("./Pages/Dashboard/GenerateIdea/IdeaReport/IdeaReport"),
);
const MyTemplates = lazy(() => import("./Pages/MyTemplates/MyTemplates"));
const MyNotifications = lazy(
  () => import("./Pages/Dashboard/MyNotifications/MyNotifications"),
);
const MyDivision = lazy(
  () => import("./Pages/Dashboard/L2/MyDivision/MyDivision"),
);
const MyThemes = lazy(() => import("./Pages/Dashboard/L2/MyThemes"));
const MyOrganisation = lazy(
  () => import("./Pages/Dashboard/L1/MyOrganisation/MyOrganisation"),
);
const MyProfile = lazy(() => import("./Pages/Dashboard/MyProfile/MyProfile"));
const AdminDashboardHome = lazy(
  () => import("./Pages/Dashboard/admin/AdminDashboardHome/AdminDashboardHome"),
);
const AdminDashboard = lazy(
  () => import("./Pages/Dashboard/admin/SuperAdminDashboard"),
);
const UserManagement = lazy(
  () => import("./Pages/Dashboard/admin/UserManagement/UserManagement"),
);
const TemplateManagement = lazy(
  () => import("./Pages/Dashboard/admin/TemplateManagement/TemplateManagement"),
);
const BillingManagement = lazy(
  () => import("./Pages/Dashboard/admin/Billing/BillingManagement"),
);
const MyTeam = lazy(() => import("./Pages/Dashboard/MyTeam/MyTeam"));
// Analytics Pages
const PotentialProjects = lazy(
  () => import("./Pages/Analytics/PotentialProjects/PotentialProjects"),
);
const MetricView = lazy(
  () => import("./Components/Analytics/ProjectMetricView/MetricView"),
);
const SPCDetails = lazy(
  () => import("./Pages/Analytics/SPCDetails/SPCDetails"),
);
const ProjectWorkload = lazy(
  () => import("./Pages/Analytics/ProjectWorkload/ProjectWorkload"),
);
const Leaderboard = lazy(
  () => import("./Pages/Analytics/Leaderboard/Leaderboard"),
);
const ProjectAnalytics = lazy(
  () => import("./Pages/Analytics/ProjectAnalytics/ProjectAnalytics"),
);
const SentimentAnalysis = lazy(
  () => import("./Pages/Analytics/SentimentAnalysis/SentimentAnalysis"),
);
const AnalyticsDashboard = lazy(
  () => import("./Pages/Analytics/AnalyticsDashboard/AnalyticsDashboard"),
);

const Security = lazy(() => import("./Pages/Security/Security"));
const BlogList = lazy(() => import("./Components/Blog/BlogList"));
const BlogDetail = lazy(() => import("./Components/Blog/BlogDetail"));
const SeoPageRoute = lazy(() => import("./Pages/SeoPages/SeoPageRoute"));

// Blog Management (Superadmin)
const BlogManagement = lazy(
  () => import("./Pages/Dashboard/admin/BlogManagement/BlogManagement"),
);
const BlogEditor = lazy(
  () => import("./Pages/Dashboard/admin/BlogManagement/BlogEditor"),
);
const BlogViewer = lazy(
  () => import("./Pages/Dashboard/admin/BlogManagement/BlogViewer"),
);

// Redirect component that preserves query params
const WhiteboardRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/dashboard/whiteboard${location.search}`} replace />;
};

function App() {
  return (
    <div>
      <Suspense fallback={<PageLoader />}>
        <SeoRouteManager />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route
            path="/login"
            element={
              <GoogleOAuthProvider
                clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}
              >
                {" "}
                <Login />{" "}
              </GoogleOAuthProvider>
            }
          />
          {/* <Route path='/register' element={ 
        <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <Register />
    </GoogleOAuthProvider>} /> */}
          <Route path="/idea-evaluation" element={<EvaluationForm />} />
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/pricing" element={<Pricing />} />
          {seoPageRoutes.map((page) => (
            <Route
              key={page.key}
              path={page.path}
              element={<SeoPageRoute pageKey={page.key} />}
            />
          ))}
          {/* Arabic /ar/ variants of all SEO pages */}
          {seoPageRoutes.map((page) => (
            <Route
              key={`ar-${page.key}`}
              path={`/ar${page.path}`}
              element={<SeoPageRoute pageKey={page.key} lang="ar" />}
            />
          ))}
          {/* Programmatic SEO pages (500+) */}
          {pSeoPageRoutes.map((page) => (
            <Route
              key={`pseo-${page.key}`}
              path={page.path}
              element={<SeoPageRoute pageKey={page.key} isPSeo />}
            />
          ))}
          <Route path="/register" element={<EarlyAccess />} />
          <Route path="/security" element={<Security />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/blogs" element={<BlogList />} />
          <Route path="/blogs/:identifier" element={<BlogDetail />} />
          <Route path="/on-boarding" element={<RedirectForm />} />
          <Route path="/password-reset" element={<PasswordResetForm />} />
          <Route
            path="/whiteboard"
            element={
              <LoggedInRoute>
                <WhiteboardRedirect />
              </LoggedInRoute>
            }
          />
          <Route path="/terms-and-conditions" element={<TermsCondition />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/schedule-consultation" element={<Consultation />} />
          <Route
            path="/dashboard"
            element={
              <LoggedInRoute>
                <EmployeeDashboard />
              </LoggedInRoute>
            }
          >
            <Route
              index
              element={
                <LoggedInRoute>
                  <EmployeeDashboardHome />
                </LoggedInRoute>
              }
            />
            <Route
              path="tasks"
              element={
                <LoggedInRoute>
                  <EmployeeTasks />
                </LoggedInRoute>
              }
            />
            <Route
              path="my-team"
              element={
                <LoggedInRoute>
                  <MyTeam />
                </LoggedInRoute>
              }
            />
            <Route
              path="feature-ideas"
              element={
                <LoggedInRoute>
                  <FeatureRequestPage />
                </LoggedInRoute>
              }
            />
            <Route
              path="projects"
              element={
                <LoggedInRoute>
                  <EmployeeProjects />
                </LoggedInRoute>
              }
            />
            <Route
              path="project-details/:id"
              element={
                <LoggedInRoute>
                  <EmployeeProjectDetails />
                </LoggedInRoute>
              }
            />
            <Route
              path="inbox"
              element={
                <LoggedInRoute>
                  <FreelancerMessenger />
                </LoggedInRoute>
              }
            />
            <Route
              path="generate-idea"
              element={
                <LoggedInRoute>
                  <GenerateIdea />
                </LoggedInRoute>
              }
            />
            <Route
              path="my-ideas"
              element={
                <LoggedInRoute>
                  <MyReports />
                </LoggedInRoute>
              }
            />
            <Route
              path="report"
              element={
                <LoggedInRoute>
                  <IdeaReport />
                </LoggedInRoute>
              }
            />
            <Route
              path="my-notifications"
              element={
                <LoggedInRoute>
                  <MyNotifications />
                </LoggedInRoute>
              }
            />
            <Route
              path="my-profile"
              element={
                <LoggedInRoute>
                  <MyProfile />
                </LoggedInRoute>
              }
            />
            <Route
              path="division"
              element={
                <LoggedInRoute>
                  <RouteForL2>
                    <MyDivision />
                  </RouteForL2>
                </LoggedInRoute>
              }
            />
            <Route
              path="theme"
              element={
                <LoggedInRoute>
                  <RouteForL2>
                    <MyThemes />
                  </RouteForL2>
                </LoggedInRoute>
              }
            />
            <Route
              path="organisation"
              element={
                <LoggedInRoute>
                  <RouteForL1>
                    <MyOrganisation />
                  </RouteForL1>
                </LoggedInRoute>
              }
            />
            {/* <Route path="analytics" element={<LoggedInRoute><ProjectAnalytics/></LoggedInRoute>} /> */}
            <Route
              path="my-drawings"
              element={
                <LoggedInRoute>
                  <MyTemplates />
                </LoggedInRoute>
              }
            />
            <Route
              path="project-analytics/:id"
              element={
                <LoggedInRoute>
                  <ProjectAnalytics />
                </LoggedInRoute>
              }
            />
            <Route
              path="analytics/metric-view/:id"
              element={
                <LoggedInRoute>
                  <MetricView />
                </LoggedInRoute>
              }
            />
            <Route
              path="analytics/details/:id"
              element={
                <LoggedInRoute>
                  <SPCDetails />
                </LoggedInRoute>
              }
            />
            <Route
              path="potential-projects"
              element={
                <LoggedInRoute>
                  <PotentialProjects />
                </LoggedInRoute>
              }
            />
            <Route
              path="my-drawings"
              element={
                <LoggedInRoute>
                  <MyTemplates />
                </LoggedInRoute>
              }
            />
            <Route
              path="analytics/project-workload/:id"
              element={
                <LoggedInRoute>
                  <ProjectWorkload />
                </LoggedInRoute>
              }
            />
            <Route
              path="leaderboard"
              element={
                <LoggedInRoute>
                  <Leaderboard />
                </LoggedInRoute>
              }
            />
            <Route
              path="sentiment-analysis"
              element={
                <LoggedInRoute>
                  <SentimentAnalysis />
                </LoggedInRoute>
              }
            />
            <Route
              path="analytics"
              element={
                <LoggedInRoute>
                  <AnalyticsDashboard />
                </LoggedInRoute>
              }
            />
            <Route
              path="whiteboard"
              element={
                <LoggedInRoute>
                  <DrawingBoard />
                </LoggedInRoute>
              }
            />

            <Route path="manage">
              <Route
                path="admin-dashboard"
                element={
                  <LoggedInRoute>
                    <RouteForAdmin>
                      <AdminDashboardHome />
                    </RouteForAdmin>
                  </LoggedInRoute>
                }
              />
              <Route
                path="users"
                element={
                  <LoggedInRoute>
                    <RouteForAdmin>
                      <UserManagement />
                    </RouteForAdmin>
                  </LoggedInRoute>
                }
              />
              <Route
                path="themes"
                element={
                  <LoggedInRoute>
                    <RouteForAdmin>
                      <ThemeManagement />
                    </RouteForAdmin>
                  </LoggedInRoute>
                }
              />
              <Route
                path="metrics"
                element={
                  <LoggedInRoute>
                    <RouteForAdmin>
                      <MetricManagement />
                    </RouteForAdmin>
                  </LoggedInRoute>
                }
              />
              <Route
                path="cost-benefits"
                element={
                  <LoggedInRoute>
                    <RouteForAdmin>
                      <CostBenefitManagement />
                    </RouteForAdmin>
                  </LoggedInRoute>
                }
              />
              <Route
                path="divisions"
                element={
                  <LoggedInRoute>
                    <RouteForAdmin>
                      <DivisionsManagement />
                    </RouteForAdmin>
                  </LoggedInRoute>
                }
              />
              <Route
                path="departments"
                element={
                  <LoggedInRoute>
                    <RouteForAdmin>
                      <DepartmentsManagement />
                    </RouteForAdmin>
                  </LoggedInRoute>
                }
              />
              <Route
                path="billing"
                element={
                  <RouteForAdmin>
                    <BillingManagement />
                  </RouteForAdmin>
                }
              />
            </Route>
          </Route>

          <Route
            path="/superadmin/dashboard"
            element={
              <LoggedInRoute>
                <RouteForSuperAdmin>
                  <AdminDashboard />
                </RouteForSuperAdmin>
              </LoggedInRoute>
            }
          >
            <Route
              index
              element={
                <LoggedInRoute>
                  <RouteForSuperAdmin>
                    <AdminDashboardHome />
                  </RouteForSuperAdmin>
                </LoggedInRoute>
              }
            />
            <Route
              path="users"
              element={
                <LoggedInRoute>
                  <RouteForSuperAdmin>
                    <UserManagement />
                  </RouteForSuperAdmin>
                </LoggedInRoute>
              }
            />
            <Route
              path="templates"
              element={
                <LoggedInRoute>
                  <RouteForSuperAdmin>
                    <TemplateManagement />
                  </RouteForSuperAdmin>
                </LoggedInRoute>
              }
            />
            <Route
              path="organisations"
              element={
                <LoggedInRoute>
                  <RouteForSuperAdmin>
                    <OrganisationManagement />
                  </RouteForSuperAdmin>
                </LoggedInRoute>
              }
            />
            <Route
              path="consultation"
              element={
                <LoggedInRoute>
                  <RouteForSuperAdmin>
                    <ConsultationList />
                  </RouteForSuperAdmin>
                </LoggedInRoute>
              }
            />
            <Route
              path="early-access"
              element={
                <LoggedInRoute>
                  <RouteForSuperAdmin>
                    <EarlyAccessList />
                  </RouteForSuperAdmin>
                </LoggedInRoute>
              }
            />
            <Route
              path="contact"
              element={
                <LoggedInRoute>
                  <RouteForSuperAdmin>
                    <ContactList />
                  </RouteForSuperAdmin>
                </LoggedInRoute>
              }
            />
            <Route
              path="my-profile"
              element={
                <LoggedInRoute>
                  <RouteForSuperAdmin>
                    <MyProfile />
                  </RouteForSuperAdmin>
                </LoggedInRoute>
              }
            />
            <Route
              path="blogs"
              element={
                <LoggedInRoute>
                  <RouteForSuperAdmin>
                    <BlogManagement />
                  </RouteForSuperAdmin>
                </LoggedInRoute>
              }
            />
            <Route
              path="blogs/create"
              element={
                <LoggedInRoute>
                  <RouteForSuperAdmin>
                    <BlogEditor />
                  </RouteForSuperAdmin>
                </LoggedInRoute>
              }
            />
            <Route
              path="blogs/edit/:id"
              element={
                <LoggedInRoute>
                  <RouteForSuperAdmin>
                    <BlogEditor />
                  </RouteForSuperAdmin>
                </LoggedInRoute>
              }
            />
            <Route
              path="blogs/view/:id"
              element={
                <LoggedInRoute>
                  <RouteForSuperAdmin>
                    <BlogViewer />
                  </RouteForSuperAdmin>
                </LoggedInRoute>
              }
            />
          </Route>
        </Routes>
      </Suspense>
    </div>
  );
}

export default App;
